import {
  HttpInterceptorFn,
  HttpResponse,
  HttpEvent
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { filter, map, shareReplay, tap } from 'rxjs/operators';

/**
 * Cache entry
 */
interface CacheEntry {
  response?: HttpResponse<any>;
  timestamp: number;
  inFlight$?: Observable<HttpResponse<any>>;
}

/**
 * In-memory HTTP cache
 */
class HttpCache {
  private cache = new Map<string, CacheEntry>();

  private readonly TTL = 60 * 60 * 1000; // 1 hour
  private readonly MAX_ENTRIES = 100;

  get(key: string): CacheEntry | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > this.TTL) {
      this.cache.delete(key);
      return null;
    }

    return entry;
  }

  set(key: string, entry: CacheEntry): void {
    if (this.cache.size >= this.MAX_ENTRIES) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }
    this.cache.set(key, entry);
  }

  clear(): void {
    this.cache.clear();
  }

  delete(key: string): void {
    this.cache.delete(key);
  }
}

const cache = new HttpCache();

/**
 * Normalize URL for caching
 */
function getCacheKey(urlWithParams: string): string {
  const url = new URL(urlWithParams, 'http://dummy');
  url.searchParams.delete('access_token');
  url.searchParams.delete('token');
  url.searchParams.delete('_');
  return `${url.pathname}${url.search}`;
}

/**
 * Optimized cache interceptor (Angular 20+)
 */
export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  // Explicit cache bypass
  if (req.headers.has('x-skip-cache')) {
    return next(req);
  }

  // Invalidate cache on mutations
  if (req.method !== 'GET') {
    cache.clear();
    return next(req);
  }

  const key = getCacheKey(req.urlWithParams);
  const cached = cache.get(key);

  // Serve cached response
  if (cached?.response) {
    return of(cached.response.clone());
  }

  // Deduplicate in-flight requests
  if (cached?.inFlight$) {
    return cached.inFlight$;
  }

  const request$ = next(req).pipe(
    filter((event: HttpEvent<any>): event is HttpResponse<any> =>
      event instanceof HttpResponse
    ),
    tap(response => {
      if (response.status === 200 && response.body != null) {
        cache.set(key, {
          response: response.clone(),
          timestamp: Date.now()
        });
      }
    }),
    shareReplay(1)
  );

  cache.set(key, {
    timestamp: Date.now(),
    inFlight$: request$
  });

  return request$;
};

/**
 * Cache utilities
 */
export function clearHttpCache(): void {
  cache.clear();
}

export function deleteFromCache(url: string): void {
  cache.delete(getCacheKey(url));
}
